import React from 'react';
import { Col, Row, Skeleton } from 'antd';
import styled from 'styled-components';
import COLOR from 'utils/constants/color';

const Root = styled.div`
  width: 100%;
  padding: 0 0 96px;
  position: relative;
`;

const TopNav = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`;

const NavPill = styled.div`
  min-height: 34px;
  min-width: 72px;
  border-radius: 18px;
  padding: 0 12px;
  display: flex;
  align-items: center;
`;

const ClassRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const OrangePill = styled.div`
  display: flex;
  align-items: center;
  min-height: 32px;
  min-width: 180px;
  padding: 4px 14px;
  border-radius: 20px;
  background: rgba(230, 126, 34, 0.28);
  border: 1px solid rgba(230, 126, 34, 0.45);
`;

const CalendarSection = styled.div`
  margin-bottom: 28px;
`;

const CalendarHeader = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const CalendarHeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const DayRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
`;

const DayCard = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 152px;
  border-radius: 12px;
  border: 1px solid ${COLOR.black800};
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${COLOR.black900};
`;

const DayCardTop = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 8px;
`;

const InnerAssignment = styled.div`
  margin-top: auto;
  align-self: center;
  width: 90%;
  min-height: 42px;
  border-radius: 8px;
  background: ${COLOR.white};
  border: 1px solid ${COLOR.black800};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px dashed ${COLOR.black800};
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const ToolbarRight = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const TableShell = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 8px;
`;

const HeaderTier = styled.div<{ $mb?: number }>`
  display: flex;
  flex-direction: row;
  gap: 2px;
  margin-bottom: ${(p) => p.$mb ?? 2}px;
  min-width: min(920px, 100%);
`;

const Tier1Orange = styled.div`
  flex: 0 0 20%;
  min-width: 100px;
  min-height: 28px;
  border-radius: 4px 0 0 0;
  background: rgba(245, 134, 23, 0.38);
`;

const Tier1Teal = styled.div`
  flex: 1;
  min-height: 28px;
  border-radius: 0 4px 0 0;
  background: rgba(3, 152, 158, 0.2);
`;

const Tier2Segment = styled.div<{ $flex: number }>`
  flex: ${(p) => p.$flex};
  min-height: 24px;
  min-width: 48px;
  border-radius: 2px;
  background: rgba(3, 152, 158, 0.12);
`;

const Tier3Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  margin-bottom: 6px;
  min-width: min(960px, 100%);
`;

const ThCorner = styled.div`
  flex: 0 0 140px;
  min-height: 36px;
  border-radius: 4px;
  background: ${COLOR.black800};
`;

const ThCell = styled.div`
  flex: 0 0 44px;
  min-height: 36px;
  border-radius: 4px;
  background: ${COLOR.black800};
`;

const ThCellWide = styled.div`
  flex: 0 0 52px;
  min-height: 36px;
  border-radius: 4px;
  background: ${COLOR.black800};
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: flex-end;
  margin-bottom: 10px;
  min-width: min(960px, 100%);
`;

const NameCell = styled.div`
  flex: 0 0 140px;
`;

const AvgCell = styled.div`
  flex: 0 0 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ScoreCell = styled.div`
  flex: 0 0 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`;

const FabWrap = styled.div`
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 20;
  pointer-events: none;
`;

const navKeys = [0, 1, 2, 3, 4];
const dayKeys = [0, 1, 2, 3, 4, 5, 6];
const daysWithInner = new Set([1, 2, 4, 6]);
const tier2Flex = [1.1, 0.9, 1.2, 0.85, 1, 0.95];
const tableRows = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const scoreCols = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const headerSmallCols = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const GradebookSkeleton: React.FC = () => (
  <Root>
    <Row gutter={[0, 0]}>
      <Col span={24}>
        <TopNav>
          {navKeys.map((k) => (
            <NavPill key={k}>
              <Skeleton.Button active style={{ width: 64 + (k % 3) * 12, height: 30, borderRadius: 18 }} />
            </NavPill>
          ))}
        </TopNav>

        <ClassRow>
          <OrangePill>
            <Skeleton active title={false} paragraph={{ rows: 1, width: '100%' }} />
          </OrangePill>
          <Skeleton.Avatar active size={28} />
          <Skeleton.Avatar active size={28} />
        </ClassRow>

        <CalendarSection>
          <CalendarHeader>
            <Skeleton.Input active style={{ width: 220, height: 22 }} />
            <CalendarHeaderRight>
              <Skeleton.Input active size="small" style={{ width: 120, height: 28, borderRadius: 6 }} />
              <Skeleton.Button active size="small" style={{ width: 32, height: 28 }} />
              <Skeleton.Button active size="small" style={{ width: 32, height: 28 }} />
            </CalendarHeaderRight>
          </CalendarHeader>
          <DayRow>
            {dayKeys.map((d) => (
              <DayCard key={d}>
                <DayCardTop>
                  <Skeleton.Avatar active size={36} shape="square" style={{ borderRadius: 6 }} />
                </DayCardTop>
                <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} style={{ marginBottom: 6 }} />
                {daysWithInner.has(d) ? (
                  <InnerAssignment>
                    <Skeleton.Input active size="small" style={{ width: '85%', height: 22, borderRadius: 4 }} />
                  </InnerAssignment>
                ) : null}
              </DayCard>
            ))}
          </DayRow>
        </CalendarSection>

        <ToolbarRow>
          <ToolbarLeft>
            <Skeleton.Input active size="small" style={{ width: 150, height: 22 }} />
            <Skeleton.Input active size="small" style={{ width: 168, height: 30, borderRadius: 20 }} />
            <Skeleton.Input active size="small" style={{ width: 132, height: 30, borderRadius: 20 }} />
          </ToolbarLeft>
          <ToolbarRight>
            <Skeleton.Button active style={{ width: 86, height: 28 }} />
            <Skeleton.Button active style={{ width: 78, height: 28 }} />
            <Skeleton.Button active style={{ width: 96, height: 28 }} />
            <Skeleton.Button active style={{ width: 88, height: 28 }} />
            <Skeleton.Button active style={{ width: 72, height: 28 }} />
          </ToolbarRight>
        </ToolbarRow>

        <TableShell>
          <HeaderTier $mb={2}>
            <Tier1Orange />
            <Tier1Teal />
          </HeaderTier>
          <HeaderTier $mb={4}>
            {tier2Flex.map((fl, i) => (
              <Tier2Segment key={i} $flex={fl} />
            ))}
          </HeaderTier>
          <Tier3Row>
            <ThCorner />
            <ThCell />
            {headerSmallCols.map((h) => (
              <ThCellWide key={h} />
            ))}
          </Tier3Row>

          {tableRows.map((r) => (
            <BodyRow key={r}>
              <NameCell>
                <Skeleton.Input active size="small" style={{ width: '100%', height: 20, maxWidth: 132 }} />
              </NameCell>
              <AvgCell>
                <Skeleton.Input active size="small" style={{ width: 36, height: 22, borderRadius: 6 }} />
              </AvgCell>
              {scoreCols.map((c) => (
                <ScoreCell key={c}>
                  <Skeleton.Avatar active size={34} shape="square" style={{ borderRadius: 8 }} />
                  {(c === 3 || c === 7) && r % 2 === 0 ? (
                    <Skeleton.Input active size="small" style={{ width: 40, height: 10, borderRadius: 2 }} />
                  ) : null}
                </ScoreCell>
              ))}
            </BodyRow>
          ))}
        </TableShell>
      </Col>
    </Row>

    <FabWrap>
      <Skeleton.Button active style={{ width: 44, height: 112, borderRadius: 22 }} />
    </FabWrap>
  </Root>
);

export default GradebookSkeleton;
